import type { StateAction } from "@entrilia/shared/types";
import { requiredValidator } from "@entrilia/shared/utilities/validators";
import type { InvestorDetails, UpdateInvestorDetailsRequest } from "../../../../api/types/investorDetailsTypes";

export interface AccountSettingsFormValues {
  name: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  state: string;
  zipCode: string;
  country: string;
  comment: string;
}

interface AccountSettingsFormErrors {
  name?: string;
  addressLine1?: string;
  city?: string;
  country?: string;
}

export interface AccountSettingsFormState {
  initialValues: AccountSettingsFormValues;
  values: AccountSettingsFormValues;
  errors: AccountSettingsFormErrors;
  isDirty: boolean;
}

export const getInitialState = (): AccountSettingsFormState => ({
  initialValues: {
    name: "",
    addressLine1: "",
    addressLine2: "",
    city: "",
    state: "",
    zipCode: "",
    country: "",
    comment: "",
  },
  values: {
    name: "",
    addressLine1: "",
    addressLine2: "",
    city: "",
    state: "",
    zipCode: "",
    country: "",
    comment: "",
  },
  errors: {},
  isDirty: false,
});

// Selectors

const detailFields: (keyof AccountSettingsFormValues)[] = [
  "name",
  "addressLine1",
  "addressLine2",
  "city",
  "state",
  "zipCode",
  "country",
];

const sanitizeFormValues = (values: AccountSettingsFormValues): AccountSettingsFormValues => ({
  name: values.name.trim(),
  addressLine1: values.addressLine1.trim(),
  addressLine2: values.addressLine2.trim(),
  city: values.city.trim(),
  state: values.state.trim(),
  zipCode: values.zipCode.trim(),
  country: values.country.trim(),
  comment: values.comment.trim(),
});

// The comment is a per-request note, not a stored investor attribute, so it must not drive the dirty flag.
const hasDetailFieldChanges = (
  initialValues: AccountSettingsFormValues,
  values: AccountSettingsFormValues
): boolean => {
  const sanitized = sanitizeFormValues(values);
  return detailFields.some((field) => initialValues[field] !== sanitized[field]);
};

export const mapFormStateToUpdateRequest = (values: AccountSettingsFormValues): UpdateInvestorDetailsRequest => {
  const { comment, ...details } = sanitizeFormValues(values);
  return { ...details, commentUserInput: comment || undefined };
};

export const validateForm = (state: AccountSettingsFormState): AccountSettingsFormErrors => {
  const nameResult = requiredValidator(state.values.name);
  const addressLine1Result = requiredValidator(state.values.addressLine1);
  const cityResult = requiredValidator(state.values.city);
  const countryResult = requiredValidator(state.values.country);

  return {
    name: nameResult.error,
    addressLine1: addressLine1Result.error,
    city: cityResult.error,
    country: countryResult.error,
  };
};

// Actions

export const loadDataAction =
  (investor: InvestorDetails): StateAction<AccountSettingsFormState> =>
  (state) => ({
    ...state,
    initialValues: sanitizeFormValues({
      name: investor.name || "",
      addressLine1: investor.addressLine1 || "",
      addressLine2: investor.addressLine2 || "",
      city: investor.city || "",
      state: investor.state || "",
      zipCode: investor.zipCode || "",
      country: investor.country || "",
      comment: "",
    }),
    values: sanitizeFormValues({
      name: investor.name || "",
      addressLine1: investor.addressLine1 || "",
      addressLine2: investor.addressLine2 || "",
      city: investor.city || "",
      state: investor.state || "",
      zipCode: investor.zipCode || "",
      country: investor.country || "",
      comment: "",
    }),
  });

export const updateFieldAction =
  (field: keyof AccountSettingsFormValues, value: string): StateAction<AccountSettingsFormState> =>
  (state) => ({
    ...state,
    values: { ...state.values, [field]: value },
    errors: { ...state.errors, [field]: undefined },
    isDirty: hasDetailFieldChanges(state.initialValues, { ...state.values, [field]: value }),
  });

export const setErrorsAction =
  (errors: AccountSettingsFormErrors): StateAction<AccountSettingsFormState> =>
  (state) => ({
    ...state,
    errors,
  });

export const resetAction = (): StateAction<AccountSettingsFormState> => (state) => ({
  initialValues: { ...state.initialValues },
  values: { ...state.initialValues },
  errors: {},
  isDirty: false,
});
